import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, TestTube2, MapPin, CreditCard, Edit2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useFetch } from '../hooks';
import { labBookingService } from '../services/api';
import { formatDate, formatCurrency } from '../utils/helpers';
import { LabStatusBadge, LAB_STATUS_CONFIG, getAllowedLabStatuses } from './LabBookings';
import Modal from '../components/Modal';

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between text-sm py-1.5 border-b border-gray-100 dark:border-gray-700 last:border-0">
    <span className="text-gray-500 dark:text-gray-400">{label}</span>
    <span className="font-medium text-gray-900 dark:text-white text-right max-w-[60%]">{value || '—'}</span>
  </div>
);

const Section = ({ title, icon: Icon, children }) => (
  <div className="card p-4 space-y-1">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-medihub-primary dark:text-medihub-highlight" />
      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{title}</p>
    </div>
    {children}
  </div>
);

const UpdateStatusModal = ({ booking, onClose, onSuccess }) => {
  const [status,  setStatus]  = useState('');
  const [report,  setReport]  = useState(null);
  const [loading, setLoading] = useState(false);

  const allowed = getAllowedLabStatuses(booking?.status);

  const handleConfirm = async () => {
    if (!status) { toast.error('Select a status'); return; }
    if (status === 'testing_completed' && !report) { toast.error('Please upload report first'); return; }
    setLoading(true);
    try {
      if (status === 'testing_completed') {
        await labBookingService.uploadReport(booking.id, report);
      } else {
        await labBookingService.updateStatus(booking.id, status);
      }
      toast.success('Status updated!');
      onSuccess();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <Modal isOpen onClose={onClose} title="Update Booking Status" size="sm">
      <div className="space-y-4">
        <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/40 rounded-xl">
          <span className="text-xs text-gray-500">Current:</span>
          <LabStatusBadge status={booking?.status} />
        </div>
        {allowed.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-2">No further status updates available</p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {allowed.map(s => (
              <button key={s} onClick={() => { setStatus(s); setReport(null); }}
                className={`py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                  status === s
                    ? 'bg-medihub-primary text-white border-medihub-primary'
                    : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}>
                {LAB_STATUS_CONFIG[s]?.label || s}
              </button>
            ))}
          </div>
        )}
        {status === 'testing_completed' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Upload Report <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept=".pdf,image/*"
              onChange={e => setReport(e.target.files[0] || null)}
              className="block w-full text-sm text-gray-600 dark:text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-medihub-light file:text-medihub-primary dark:file:bg-medihub-dark/30 dark:file:text-medihub-highlight cursor-pointer"
            />
            {report && <p className="text-xs text-green-600 dark:text-green-400 mt-1">✓ {report.name}</p>}
          </div>
        )}
        <div className="flex justify-end gap-3 pt-1">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleConfirm}
            disabled={!status || loading || (status === 'testing_completed' && !report)}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? 'Updating…' : 'Update Status'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const LabBookingDetail = () => {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const [showStatusModal, setShowStatusModal] = useState(false);
  const { data: booking, loading, refetch } = useFetch(() => labBookingService.getById(id), [id]);

  if (loading) return (
    <div className="space-y-4">
      {Array(3).fill(0).map((_, i) => <div key={i} className="card h-32 animate-pulse bg-gray-200 dark:bg-gray-700 rounded-xl" />)}
    </div>
  );

  if (!booking) return (
    <div className="text-center py-16">
      <p className="text-gray-500">Booking not found</p>
      <button onClick={() => navigate('/lab-bookings')} className="btn-primary mt-4">Back to Bookings</button>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/lab-bookings')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Booking #{booking.id}</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">{booking.order_uid} · {formatDate(booking.created_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <LabStatusBadge status={booking.status} />
          {booking.status !== 'cancelled' && booking.status !== 'testing_completed' && (
            <button onClick={() => setShowStatusModal(true)} className="btn-secondary flex items-center gap-1.5 text-sm">
              <Edit2 className="w-3.5 h-3.5" />Update Status
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Test Info */}
        <Section title="Test Details" icon={TestTube2}>
          <div className="flex items-center gap-3 mb-3">
            {booking.lab_test_image
              ? <img src={booking.lab_test_image} alt={booking.lab_test_name} className="w-14 h-14 rounded-xl object-cover border border-gray-200 dark:border-gray-700" />
              : <div className="w-14 h-14 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-center justify-center"><TestTube2 className="w-6 h-6 text-blue-500" /></div>
            }
            <div>
              <p className="font-semibold text-gray-900 dark:text-white">{booking.lab_test_name}</p>
              <p className="text-xs text-gray-500">{booking.category_name}</p>
            </div>
          </div>
          <InfoRow label="Booking Date"     value={formatDate(booking.booking_date)} />
          <InfoRow label="Slot"             value={booking.slot_start && booking.slot_end ? `${booking.slot_start?.slice(0,5)} – ${booking.slot_end?.slice(0,5)}` : null} />
          <InfoRow label="Home Collection"  value={booking.home_collection ? 'Yes' : 'No'} />
          <InfoRow label="Description"      value={booking.description} />
          {booking.cancel_reason && <InfoRow label="Cancel Reason" value={booking.cancel_reason} />}
        </Section>

        {/* Patient Info */}
        <Section title="Patient Details" icon={User}>
          <div className="flex items-center gap-3 mb-3">
            {booking.patient_img
              ? <img src={booking.patient_img} alt={booking.patient_name} className="w-12 h-12 rounded-full object-cover border border-gray-200 dark:border-gray-700" />
              : <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center text-lg font-bold text-gray-400">{booking.patient_name?.[0]}</div>
            }
            <div>
              <p className="font-semibold text-gray-900 dark:text-white">{booking.patient_name}</p>
              <p className="text-xs text-gray-500">{booking.patient_mobile}</p>
            </div>
          </div>
          <InfoRow label="Email"  value={booking.patient_email} />
          <InfoRow label="Mobile" value={booking.patient_mobile} />
        </Section>

        {/* Payment Info */}
        <Section title="Payment Details" icon={CreditCard}>
          <InfoRow label="Order ID"       value={booking.order_uid} />
          <InfoRow label="Original Price" value={formatCurrency(booking.total_original)} />
          <InfoRow label="Discount"       value={formatCurrency(booking.total_discount)} />
          <InfoRow label="Total Paid"     value={formatCurrency(booking.total_amount)} />
          <InfoRow label="Payment Method" value={booking.payment_method?.toUpperCase()} />
          <InfoRow label="Order Status"   value={booking.order_status} />
        </Section>

        {/* Address */}
        <Section title="Collection Address" icon={MapPin}>
          <InfoRow label="Address" value={booking.address_line} />
          <InfoRow label="City"    value={booking.city} />
          <InfoRow label="State"   value={booking.state} />
          <InfoRow label="Pincode" value={booking.pincode} />
          <InfoRow label="Mobile"  value={booking.address_mobile} />
        </Section>
      </div>

      {showStatusModal && (
        <UpdateStatusModal
          booking={booking}
          onClose={() => setShowStatusModal(false)}
          onSuccess={refetch}
        />
      )}
    </div>
  );
};

export default LabBookingDetail;
